import type { JSX } from "solid-js";
import { pieceDef, type PieceTypeId } from "../../core";
import { hideTooltip, moveTooltip, showTooltip } from "../../app/tooltip";

/**
 * Renders ONE half of a continuous two-cell terrain glyph (Valley / Ridge).
 *
 * The two cells of a footprint piece each hold the same piece, so naively
 * drawing the full SVG in both cells produces a visible seam down the middle.
 * Instead each cell paints a full-bleed background of the wide (2:1) / tall
 * (1:2) art and shifts the background-position so it shows exactly its half;
 * placed in adjacent cells the halves line up into one seamless image.
 *
 *   "h": 200% x 100%, position 0%/50% = left half, 100%/50% = right half
 *   "v": 100% x 200%, position 50%/0% = top half, 50%/100% = bottom half
 */
export function TerrainTile(props: {
  type: PieceTypeId;
  /** Which half of the span this cell shows. */
  half: "left" | "right" | "top" | "bottom";
  class?: string;
  style?: JSX.CSSProperties;
}) {
  const horizontal = () => props.half === "left" || props.half === "right";

  const position = () => {
    switch (props.half) {
      case "left":
        return "0% 50%";
      case "right":
        return "100% 50%";
      case "top":
        return "50% 0%";
      case "bottom":
        return "50% 100%";
    }
  };

  const tip = () => {
    const def = pieceDef(props.type);
    return `${def.name} — ${def.description ?? `moves like a ${def.name.toLowerCase()}`}`;
  };

  return (
    <div
      class={`terrain-tile ${props.class ?? ""}`}
      role="img"
      aria-label={props.type}
      style={{
        "background-image": `url(/pieces/${props.type}.svg)`,
        "background-size": horizontal() ? "200% 100%" : "100% 200%",
        "background-position": position(),
        ...props.style,
      }}
      onMouseEnter={(e) => showTooltip(tip(), e.clientX, e.clientY, props.type)}
      onMouseMove={(e) => moveTooltip(e.clientX, e.clientY)}
      onMouseLeave={hideTooltip}
    />
  );
}
